import { fetchWrapper } from '@/helpers';
import { useUsersStore } from '@/stores/users.store';
import { useNotificationStore } from '@/stores/notification.store';
import { defineStore } from 'pinia';
import { ref } from 'vue';

// Firebase imports
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import SystemApi from '@/api/systemApi';

const systemApiService = new SystemApi();

const baseUrl = `${process.env.VUE_APP_PROXY_SERVER}/api/user`;

let firebaseInitialized = false;

const EMPTY_FIREBASE_CONFIG = {
  apiKey: '',
  authDomain: '',
  projectId: '',
  storageBucket: '',
  messagingSenderId: '',
  appId: '',
  measurementId: '',
  vapidKey: '',
};

const firebaseConfig = ref(EMPTY_FIREBASE_CONFIG);

async function fetchFirebaseConfig() {
  const response = await systemApiService.getSystemConfigByKey(
    'FIREBASE_CONFIG'
  );

  if (response?.value) {
    firebaseConfig.value = {
      ...EMPTY_FIREBASE_CONFIG,
      ...JSON.parse(response.value),
    };
  } else {
    firebaseConfig.value = { ...EMPTY_FIREBASE_CONFIG };
  }
}

export const useAuthStore = defineStore(
  `${process.env.VUE_APP_NAME}_authStore`,
  {
    id: 'auth',
    state: () => ({
      // initialize state from local storage to enable user to stay logged in
      user: JSON.parse(
        localStorage.getItem(`${process.env.VUE_APP_NAME}_user`)
      ),
      returnUrl: null,
      currentStatus: null,
    }),
    actions: {
      async login(email, password) {
        try {
          const obj = { username: email, password: password };
          const user = await fetchWrapper.post(`${baseUrl}/authenticate`, obj);

          // update pinia state
          this.user = user;

          // store user details and jwt in local storage to keep user logged in between page refreshes
          localStorage.clear();
          localStorage.setItem(
            `${process.env.VUE_APP_NAME}_user`,
            JSON.stringify(user)
          );

          // Initialize Firebase and register FCM token
          if (!firebaseInitialized) {
            this.initializeFirebase();
          }

          const usersStore = useUsersStore();

          await usersStore.fetchPageConfigs();

          return true;
        } catch (error) {
          return error;
        }
      },
      async fetchPermission() {
        try {
          var result = {
            isUpdatePremission: false,
            isUpdateProject: false,
            isActive: false,
          };
          var isUpdate = false;
          var url = `${baseUrl}/UserPermission?Id=${this.user.id}`;

          const response = await fetchWrapper.get(url);

          result.isActive = response.isActive;

          if (!compareArrays(this.user.permissions, response.permissions)) {
            isUpdate = true;
            result.isUpdatePremission = true;

            this.user.permissions = response.permissions;
          }

          if (!compareArrays(this.user.projectIds, response.projectIds)) {
            isUpdate = true;
            result.isUpdateProject = true;
            this.user.projectIds = response.projectIds;
          }

          if (isUpdate) {
            localStorage.setItem(
              `${process.env.VUE_APP_NAME}_user`,
              JSON.stringify(this.user)
            );
          }

          return result;
        } catch (error) {
          console.error(error);
        }
      },
      logout() {
        fetchWrapper.post(`${baseUrl}/Logout`);
        this.user = null;
        localStorage.removeItem(`${process.env.VUE_APP_NAME}_user`);
        localStorage.clear();
        sessionStorage.clear();
      },
      async initializeFirebase() {
        if (firebaseInitialized) return;

        await fetchFirebaseConfig();

        firebaseInitialized = true;

        this.currentStatus = await this.getNotificationValue();

        const config = firebaseConfig.value;

        if (!config?.projectId) {
          console.error('Firebase config missing projectId', config);
          firebaseInitialized = false;
          return;
        }

        // Initialize Firebase
        const firebaseApp = !getApps().length
          ? initializeApp(config)
          : getApps()[0];

        // Initialize Firebase Messaging and get the messaging instance
        const messaging = getMessaging(firebaseApp);

        if (Notification.permission === 'default') {
          try {
            const permission = await Notification.requestPermission();
            this.currentStatus = permission;
          } catch (error) {
            console.error('Error requesting notification permission:', error);
            this.currentStatus = 'denied';
            return;
          }
        }

        // Register the service worker
        if (this.currentStatus === 'granted') {
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker
              .register('/firebase-messaging-sw.js')
              .then((registration) => {
                // Get the messaging token
                getToken(messaging, {
                  vapidKey: config.vapidKey,
                  serviceWorkerRegistration: registration,
                })
                  .then((currentToken) => {
                    if (currentToken) {
                      // Store and register the token using the notification store
                      const notificationStore = useNotificationStore();
                      notificationStore.registerToken(currentToken);
                    } else {
                      console.log(
                        'No registration token available. Request permission to generate one.'
                      );
                    }
                  })
                  .catch((err) => {
                    console.log(
                      'An error occurred while retrieving token. ',
                      err
                    );
                  });

                // Handle incoming messages
                onMessage(messaging, (payload) => {
                  console.log('Message received. ', payload);
                  const notificationStore = useNotificationStore();
                  notificationStore.fetchNotifications(1, 10);
                });
              })
              .catch((error) => {
                console.error('Service Worker registration failed:', error);
              });
          }
        }
      },
      checkAndInitializeFirebase() {
        if (this.user?.token && !firebaseInitialized) {
          this.initializeFirebase();
        }
      },
      async getNotificationValue() {
        try {
          return Notification.permission;
        } catch (err) {
          console.error('Notification Error', err);
          return null;
        }
      },
    },
  }
);

const compareArrays = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b);
};
